import type { LocalDate } from "@js-joda/core";
import { PaymentMethod } from "model/Gig";
import { SalaryType, WorkerEmploymentType } from "model/Salary";
import { isSettingEnabled } from "model/settings.logic";
import {
  EMPLOYMENT_TYPE_CONSULTANT,
  EMPLOYMENT_TYPE_PROJECT_EMPLOYEE,
  EMPLOYMENT_TYPE_SIMFLEXITY_EMPLOYEE,
  EMPLOYMENT_TYPE_TRAINEE,
} from "model/OrganizationalStructure";

export interface EmploymentUiRules {
  readonly canSelectSimflexityEmployee: boolean;
  readonly canSelectSimflexityPay: boolean;
  readonly isConsultantEmployee: boolean;
  readonly isEndDateRequiredOnSave: boolean;
  readonly isHourlyTemporaryEmployee: boolean;
  readonly isSimflexityEmployee: boolean;
  readonly isTraineeEmployee: boolean;
}

interface CompanySettingValue {
  readonly key: string;
  readonly value?: string | null;
}

interface EmploymentUiRulesParams {
  readonly companySettings: CompanySettingValue[];
  readonly employmentType: string;
  readonly salaryType: string;
  readonly subscription?: string;
}

/**
 * Employment form rules.
 *
 * Rules 1-6 are enforced continuously by `withNormalizedEmploymentFormByRules`,
 * which both panels re-apply from an effect on every render. A value that
 * violates them cannot be entered or kept.
 * 1. Simflexity, consultant, trainee and hourly temporary workers
 *    (TemporaryWorker / ExclusiveTemporaryWorker) must use hourly salary (Hourly).
 * 2. Simflexity employee must use SimflexityPay as payment type.
 * 3. Consultant and trainee must use employee category = "BlueCollar".
 * 4. SimflexityPay may be selected for Employee, Introductory, temporary and
 *    project employments only when:
 *    - the company has a Flex subscription,
 *    - SimflexityPay is enabled in company settings,
 *    - salary type is Hourly.
 * 5. Starter subscriptions cannot create Simflexity employees.
 * 6. If SimflexityPay is not allowed in the current context, payment type is normalized to "None".
 * 7. End date is required on save for introductory, project and trainee
 *    employments.
 *
 * Rule 8 is a prefill, not an enforced rule. `withEmploymentTypeChangePrefill`
 * runs from the field setter when the user picks a new employment type, and when
 * a missing start date is later supplied for introductory employment. The
 * prefilled values stay editable afterwards. It deliberately does not live in the
 * enforcement effect above — re-applying it every render would pin the fields.
 * 8. Picking employment type "Employee" or "Introductory" in the dropdown
 *    overwrites the contract terms with the statutory minimums (30 notice days,
 *    25 vacation days) and the company address as workplace. Introductory also
 *    suggests an end date six months after the selected start date.
 *
 * Employment templates never reach rule 8: both panels apply a template by writing
 * the whole employment form at once, not through the field setters, so a template's
 * own contract terms always stand.
 */

/**
 * Derives UI rule flags for employment/salary form behavior from rule set above.
 */
export function toEmploymentUiRules({
  companySettings,
  employmentType,
  salaryType,
  subscription,
}: EmploymentUiRulesParams): EmploymentUiRules {
  const isSimflexityEmployee =
    employmentType === EMPLOYMENT_TYPE_SIMFLEXITY_EMPLOYEE;
  const isTraineeEmployee = employmentType === EMPLOYMENT_TYPE_TRAINEE;
  const isConsultantEmployee = employmentType === EMPLOYMENT_TYPE_CONSULTANT;
  const isIntroductoryEmployee =
    employmentType === WorkerEmploymentType.INTRODUCTORY;
  const isProjectEmployee = employmentType === EMPLOYMENT_TYPE_PROJECT_EMPLOYEE;
  const isHourlyTemporaryEmployee =
    employmentType === WorkerEmploymentType.TEMPORARY_WORKER ||
    employmentType === WorkerEmploymentType.EXCLUSIVE_TEMPORARY_WORKER;
  const isEndDateRequiredOnSave = isEmploymentEndDateRequired(employmentType);
  const isFlexCompany = subscription?.includes("Flex") === true;
  const isStarterCompany = subscription?.includes("Starter") === true;
  const isSimflexityPayEmploymentType =
    employmentType === WorkerEmploymentType.EMPLOYEE ||
    isIntroductoryEmployee ||
    isHourlyTemporaryEmployee ||
    isProjectEmployee;

  const hasSimflexityPay = isSettingEnabled(companySettings, "SimflexityPay");

  const canSelectSimflexityPay =
    isFlexCompany &&
    hasSimflexityPay &&
    isSimflexityPayEmploymentType &&
    salaryType === SalaryType.Hourly;

  return {
    canSelectSimflexityEmployee: !isStarterCompany,
    canSelectSimflexityPay,
    isConsultantEmployee,
    isEndDateRequiredOnSave,
    isHourlyTemporaryEmployee,
    isSimflexityEmployee,
    isTraineeEmployee,
  };
}

/**
 * Applies the derived employment rules to current form values and returns a normalized copy.
 *
 * What this function may change:
 * - `salaryType`: forced to `Hourly` for Simflexity, consultant, trainee and
 *   hourly temporary workers.
 * - `employeeCategory`: forced to `"BlueCollar"` for consultants and trainees.
 * - `paymentType`:
 *   - forced to `"SimflexityPay"` for Simflexity employees,
 *   - reset to `"None"` when SimflexityPay is selected but not allowed by rules.
 *
 * What this function does not do:
 * - It does not mutate the input object.
 * - It does not validate required fields or return errors.
 * - It only enforces cross-field consistency based on `rules`.
 */
export const withNormalizedEmploymentFormByRules = (
  employmentDetails: {
    salaryType: string;
    employeeCategory: string;
    paymentType: string;
  },
  rules: EmploymentUiRules,
): {
  salaryType: string;
  employeeCategory: string;
  paymentType: string;
} => {
  let nextSalaryType = employmentDetails.salaryType;
  let nextEmployeeCategory = employmentDetails.employeeCategory;
  let nextPaymentType = employmentDetails.paymentType;

  if (
    (rules.isSimflexityEmployee ||
      rules.isConsultantEmployee ||
      rules.isTraineeEmployee ||
      rules.isHourlyTemporaryEmployee) &&
    nextSalaryType !== SalaryType.Hourly
  ) {
    nextSalaryType = SalaryType.Hourly;
  }

  if (rules.isConsultantEmployee || rules.isTraineeEmployee) {
    nextEmployeeCategory = "BlueCollar";
  }

  if (
    rules.isSimflexityEmployee &&
    nextPaymentType !== PaymentMethod.SIMFLEXITY_PAY
  ) {
    nextPaymentType = PaymentMethod.SIMFLEXITY_PAY;
  }

  if (
    !rules.isSimflexityEmployee &&
    nextPaymentType === PaymentMethod.SIMFLEXITY_PAY &&
    !rules.canSelectSimflexityPay
  ) {
    nextPaymentType = PaymentMethod.NONE;
  }

  return {
    salaryType: nextSalaryType,
    employeeCategory: nextEmployeeCategory,
    paymentType: nextPaymentType,
  };
};

export interface EmploymentContractTerms {
  readonly noticeDays: string;
  readonly vacationDays: string;
  readonly workplace: string;
}

interface EmploymentFormWithPeriod extends EmploymentContractTerms {
  readonly endDate: LocalDate | null;
  readonly employmentType?: string;
  readonly startDate: LocalDate | null;
}

interface EmploymentTypeChangePrefillParams {
  readonly companyAddress?: string | null;
  readonly employmentType: string;
}

interface WithEmploymentTypeChangePrefillParams<
  TForm extends EmploymentFormWithPeriod,
> {
  readonly changedField: keyof TForm;
  readonly changedValue: TForm[keyof TForm];
  readonly companyAddress?: string | null;
  readonly form: TForm;
}

const EMPLOYEE_PREFILL_NOTICE_DAYS = "30";
const EMPLOYEE_PREFILL_VACATION_DAYS = "25";

export function isEmploymentEndDateRequired(employmentType?: string): boolean {
  return (
    employmentType === WorkerEmploymentType.INTRODUCTORY ||
    employmentType === EMPLOYMENT_TYPE_PROJECT_EMPLOYEE ||
    employmentType === EMPLOYMENT_TYPE_TRAINEE
  );
}

/**
 * Rule 8: the contract terms to prefill for a newly picked employment type,
 * or `null` when the type has no prefill.
 */
export function toEmploymentTypeChangePrefill({
  companyAddress,
  employmentType,
}: EmploymentTypeChangePrefillParams): EmploymentContractTerms | null {
  if (
    employmentType !== WorkerEmploymentType.EMPLOYEE &&
    employmentType !== WorkerEmploymentType.INTRODUCTORY
  ) {
    return null;
  }

  return {
    noticeDays: EMPLOYEE_PREFILL_NOTICE_DAYS,
    vacationDays: EMPLOYEE_PREFILL_VACATION_DAYS,
    workplace: companyAddress?.trim() ?? "",
  };
}

/**
 * Applies the rule 8 prefill and period cleanup to an already-updated form.
 */
export function withEmploymentTypeChangePrefill<
  TForm extends EmploymentFormWithPeriod,
>({
  changedField,
  changedValue,
  companyAddress,
  form,
}: WithEmploymentTypeChangePrefillParams<TForm>): TForm {
  if (
    changedField === "startDate" &&
    form.employmentType === WorkerEmploymentType.INTRODUCTORY &&
    form.startDate != null &&
    form.endDate == null
  ) {
    return { ...form, endDate: form.startDate.plusMonths(6) };
  }

  if (changedField !== "employmentType" || typeof changedValue !== "string") {
    return form;
  }

  const formWithValidEndDate = isEmploymentEndDateRequired(changedValue)
    ? form
    : { ...form, endDate: null };

  const prefill = toEmploymentTypeChangePrefill({
    companyAddress,
    employmentType: changedValue,
  });
  if (prefill === null) return formWithValidEndDate;

  if (changedValue === WorkerEmploymentType.INTRODUCTORY) {
    return {
      ...formWithValidEndDate,
      ...prefill,
      endDate: formWithValidEndDate.startDate?.plusMonths(6) ?? null,
    };
  }

  return { ...formWithValidEndDate, ...prefill };
}
